import React, { useState } from 'react';
import styled from 'styled-components';

interface VideoDetailsFormProps {
  onSubmit: (data: VideoFormData) => void;
}

export interface VideoFormData {
  title: string;
  description: string;
  tags: string[];
  isProductReel: boolean;
  productUrl?: string;
}

const FormContainer = styled.form`
  width: 100%;
  max-width: 500px;
  margin: 24px auto 0;
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: white;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  background-color: #2A2438;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px 16px;
  color: white;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #9333EA;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  background-color: #2A2438;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px 16px;
  color: white;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #9333EA;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const Tag = styled.div`
  background-color: rgba(147, 51, 234, 0.1);
  color: #9333EA;
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 12px;
  display: flex;
  align-items: center;
  
  button {
    background: none;
    border: none;
    color: #9333EA;
    margin-left: 6px;
    cursor: pointer;
    font-size: 14px;
    line-height: 1;
    padding: 0;
  }
`;

const TagInput = styled.input`
  background-color: transparent;
  border: none;
  color: white;
  font-size: 14px;
  padding: 0;
  margin-top: 8px;
  width: 100%;
  
  &:focus {
    outline: none;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const Checkbox = styled.input`
  margin-right: 8px;
  cursor: pointer;
  accent-color: #9333EA;
`;

const CheckboxLabel = styled.label`
  color: white;
  font-size: 14px;
  cursor: pointer;
`;

const SubmitButton = styled.button`
  background-color: #9333EA;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 32px;
  align-self: center;
  min-width: 180px;
  
  &:hover {
    background-color: #A855F7;
  }
  
  &:disabled {
    background-color: rgba(147, 51, 234, 0.5);
    cursor: not-allowed;
  }
`;

const VideoDetailsForm: React.FC<VideoDetailsFormProps> = ({ onSubmit }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [isProductReel, setIsProductReel] = useState(false);
  const [productUrl, setProductUrl] = useState('');
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      title,
      description,
      tags,
      isProductReel,
      productUrl: isProductReel ? productUrl : undefined
    });
  };
  
  const handleAddTag = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!tags.includes(tagInput.trim())) {
        setTags([...tags, tagInput.trim()]);
      }
      setTagInput('');
    }
  };
  
  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };
  
  return (
    <FormContainer onSubmit={handleSubmit}>
      <FormGroup>
        <Label htmlFor="title">Title</Label>
        <Input 
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Add a title that describes your video"
          required
        />
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="description">Description</Label>
        <TextArea 
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Tell viewers about your video"
          rows={3}
        />
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="tags">Tags</Label>
        <TagsContainer>
          {tags.map((tag, index) => (
            <Tag key={index}>
              {tag}
              <button type="button" onClick={() => handleRemoveTag(tag)}>×</button>
            </Tag>
          ))}
        </TagsContainer>
        <TagInput 
          id="tags"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={handleAddTag}
          placeholder="Add tags (press Enter after each tag)"
        />
      </FormGroup>
      
      <CheckboxContainer>
        <Checkbox 
          id="isProductReel"
          type="checkbox"
          checked={isProductReel}
          onChange={(e) => setIsProductReel(e.target.checked)}
        />
        <CheckboxLabel htmlFor="isProductReel">This is a product reel</CheckboxLabel>
      </CheckboxContainer>
      
      {isProductReel && (
        <FormGroup>
          <Label htmlFor="productUrl">Product URL</Label>
          <Input 
            id="productUrl"
            type="url"
            value={productUrl}
            onChange={(e) => setProductUrl(e.target.value)}
            placeholder="Enter the product URL"
            required={isProductReel}
          />
        </FormGroup>
      )}
      
      <SubmitButton 
        type="submit" 
        disabled={!title.trim()}
      >
        Publish Video
      </SubmitButton>
    </FormContainer>
  );
};

export default VideoDetailsForm;
